import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import RechargeQrModal, { rechargeTimeLeft } from "@app/components/recharge-qr-modal";
import { addToast } from "@app/components/toast/toast.service";
import BankService from "@app/services/http/bank.service";
import RechargeService from "@app/services/http/recharge.service";
import { openPortalDialog } from "@app/services/modal.service";
import SocketService from "@app/services/socket.service";
import { GlobalState } from "@app/store";
import { Bank, Recharge, RechargeSummary } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { IcArrowLeft, IcBank, IcCheck, IcClock, IcInfo, IcQr, IcWallet } from "@app/components/icons";
import { btn, btnGhost, btnLg, btnPrimary, btnSm } from "@app/components/ui";
import BalanceHistory from "./balance-history";

/** Giống createRechargeValidationSchema và kiểm tra ở backend (bội số 1.000đ) */
const MIN = 50000;
const MAX = 100000000;
const QUICK = [50000, 100000, 200000, 500000, 1000000, 2000000];

/** Trang gọi tới Nạp tiền kèm ?amount=&from= (thiếu tiền khi thuê) */
const FROM: Record<string, string> = {
  rent: "thuê máy tính",
  cloud: "tạo Cloud",
  vps: "thuê VPS",
  extend: "gia hạn máy",
  extend_cloud: "gia hạn Cloud",
  extend_vps: "gia hạn VPS",
};

const selectMyInfo = (state: GlobalState) => state.myInfo;

/** Trang Nạp tiền: tạo lệnh nạp (VietQR), số dư và Biến động số dư. API nạp giữ nguyên như modal cũ. */
function PaymentHistory() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { myInfo } = useSelector(selectMyInfo);
  const balance = myInfo?.balance || 0;

  const need = Math.max(0, Number(params.get("amount")) || 0);
  const from = params.get("from") || "";
  // số dư cần đạt = số dư lúc mở trang + số tiền còn thiếu
  const targetRef = useRef<number>();

  if (need && targetRef.current === undefined && myInfo?._id) targetRef.current = balance + need;

  const [amount, setAmount] = useState(() =>
    need ? String(Math.min(MAX, Math.max(MIN, Math.ceil(need / 1000) * 1000))) : ""
  );
  const [error, setError] = useState("");
  const [banks, setBanks] = useState<Bank[]>([]);
  const [bankId, setBankId] = useState("");
  const [banksLoading, setBanksLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [summary, setSummary] = useState<RechargeSummary>();
  const [pendingRecharge, setPendingRecharge] = useState<Recharge>();
  const [reload, setReload] = useState(0);
  const [, setTick] = useState(0);

  const { subscribeOnce, subscribeUntilDestroy } = useObservable();
  const money = Number(amount) || 0;
  const valid = money >= MIN && money <= MAX;

  const refresh = () => setReload((n) => n + 1);

  // tổng hợp + lệnh nạp đang chờ thanh toán
  useEffect(() => {
    subscribeUntilDestroy(RechargeService.getSummary(), setSummary);
    subscribeUntilDestroy(RechargeService.getRecharges(1, { flow: "pending" }), (res) =>
      setPendingRecharge((res?.data || []).find((r) => rechargeTimeLeft(r) > 0))
    );
  }, [reload]);

  // nạp thành công / thất bại (tool gửi socket): tải lại. App đã hiện thông báo và cập nhật số dư
  useEffect(() => {
    const sub = SocketService.onRechargeResult().subscribe(() => refresh());

    return () => sub.unsubscribe();
  }, []);

  // đồng hồ đếm ngược của lệnh nạp đang chờ
  useEffect(() => {
    if (!pendingRecharge) return;

    const timer = setInterval(() => {
      setTick((n) => n + 1);

      if (rechargeTimeLeft(pendingRecharge) <= 0) {
        clearInterval(timer);
        setPendingRecharge(undefined);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [pendingRecharge]);

  // ngân hàng nhận theo số tiền (như modal cũ), chờ 400ms sau khi gõ
  useEffect(() => {
    if (!valid) {
      setBanks([]);

      return;
    }

    setBanksLoading(true);

    const timer = setTimeout(() => {
      subscribeUntilDestroy(BankService.getBanks(money), (list) => {
        setBanks(list || []);
        setBankId((id) => (list?.some((b) => b._id === id) ? id : list?.[0]?._id || ""));
        setBanksLoading(false);
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [money]);

  const onAmountChange = (value: string) => {
    const digits = value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9);

    setAmount(digits);
    setError(Number(digits) > MAX ? `Mỗi lần nạp tối đa ${formatMoneyVN(MAX)} VND` : "");
  };

  const openQr = (recharge: Recharge) => {
    openPortalDialog(RechargeQrModal, { recharge })
      .afterClosed()
      .subscribe((data) => {
        if (data?.isCancel) {
          subscribeOnce(RechargeService.cancelRecharge(recharge._id), () => {
            addToast({ text: `${t("Đã huỷ lệnh nạp")} ${formatMoneyVN(recharge.money)} VND` });
            refresh();
          });

          return;
        }

        if (data?.isCompleted) {
          addToast({ text: t("Đã ghi nhận, số dư sẽ được cộng tự động sau khi hệ thống nhận được tiền") });
        }

        refresh();
      });
  };

  const handleCreate = () => {
    const message = !money
      ? "Vui lòng nhập số tiền cần nạp"
      : money < MIN
      ? `Bạn cần nạp tối thiểu ${formatMoneyVN(MIN)} VND`
      : money > MAX
      ? `Mỗi lần nạp tối đa ${formatMoneyVN(MAX)} VND`
      : money % 1000 !== 0
      ? "Số tiền phải là bội số của 1.000 VND"
      : !bankId
      ? "Chưa có ngân hàng nhận cho mức nạp này, vui lòng chọn số tiền khác"
      : "";

    setError(message);

    if (message) return;

    setBusy(true);

    RechargeService.createRecharge({ money: String(money), bank_id: bankId }).subscribe({
      next: (res) => {
        setBusy(false);
        refresh();

        if (res?.link_payment) openQr(res as unknown as Recharge);
      },
      error: () => setBusy(false),
    });
  };

  const target = targetRef.current;
  const stillNeed = target !== undefined ? target - balance : 0;
  const fromName = FROM[from] || "tiếp tục";

  return (
    <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4 max-[760px]:gap-[18px]">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">{t("Nạp tiền")}</h1>
        <p className="m-0 mt-1 max-w-[64ch] text-muted">
          {t("Chuyển khoản qua mã VietQR. Số dư được cộng tự động khi hệ thống nhận được tiền.")}
        </p>
      </div>

      {need > 0 && target !== undefined && (
        <div
          className={clsx(
            "flex flex-wrap items-center gap-3 rounded-card border px-4 py-3",
            stillNeed > 0 ? "border-brand-line bg-brand-soft" : "border-ok bg-ok-soft"
          )}
        >
          {stillNeed > 0 ? <IcInfo className="h-5 w-5 flex-none text-brand" /> : <IcCheck className="h-5 w-5 flex-none text-ok" />}
          <div className="min-w-0 flex-1 text-[13.5px]">
            <b className="block">
              {stillNeed > 0
                ? `${t("Bạn cần nạp thêm")} ${formatMoneyVN(stillNeed)} VND ${t("để")} ${t(fromName)}.`
                : `${t("Số dư đã đủ để")} ${t(fromName)}.`}
            </b>
            <span className="text-muted">
              {stillNeed > 0
                ? t("Số tiền đã được điền sẵn, bạn có thể đổi nếu muốn nạp nhiều hơn.")
                : t("Quay lại để hoàn tất đơn của bạn.")}
            </span>
          </div>
          <button type="button" className={clsx(btn, btnSm, stillNeed > 0 ? btnGhost : btnPrimary)} onClick={() => navigate(-1)}>
            <IcArrowLeft className="h-4 w-4" />
            {t("Quay lại")}
          </button>
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_340px] items-start gap-5 max-[1100px]:grid-cols-1">
        {/* Tạo lệnh nạp */}
        <section className="flex flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
          <div className="flex items-center gap-2 border-b border-line px-5 py-3.5 max-[760px]:px-3.5">
            <IcWallet className="h-5 w-5 text-brand" />
            <h2 className="text-[17px] font-bold">{t("Tạo lệnh nạp")}</h2>
          </div>

          <div className="flex flex-col gap-4 px-5 py-4 max-[760px]:p-4">
            {pendingRecharge && (
              <div className="flex flex-wrap items-center gap-2.5 rounded-[10px] bg-warn-soft px-3 py-2.5 text-[13px] text-warn">
                <IcClock className="h-4 w-4 flex-none" />
                <span className="min-w-0 flex-1">
                  {t("Bạn có lệnh nạp")} <b className="tabular-nums">{formatMoneyVN(pendingRecharge.money)} VND</b>{" "}
                  {t("đang chờ thanh toán, còn")}{" "}
                  <b className="tabular-nums">
                    {new Date(rechargeTimeLeft(pendingRecharge)).toISOString().slice(14, 19)}
                  </b>
                  .
                </span>
                <button type="button" className={clsx(btn, btnGhost, btnSm)} onClick={() => openQr(pendingRecharge)}>
                  <IcQr className="h-4 w-4" />
                  {t("Mở mã QR")}
                </button>
              </div>
            )}

            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
              {t("Số tiền nạp")}
              <div
                className={clsx(
                  "flex h-12 items-center rounded-[10px] border bg-surface px-3 focus-within:border-brand focus-within:shadow-[0_0_0_3px_var(--brand-soft)]",
                  error ? "border-bad" : "border-line"
                )}
              >
                <input
                  inputMode="numeric"
                  autoComplete="off"
                  value={money ? formatMoneyVN(money) : ""}
                  placeholder={t("Nhập số tiền, tối thiểu 50.000")}
                  onChange={(e) => onAmountChange(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  className="min-w-0 flex-1 border-0 bg-transparent text-lg font-bold tabular-nums text-fg outline-none placeholder:text-[14px] placeholder:font-normal"
                />
                <em className="text-sm font-semibold not-italic text-faint">VND</em>
              </div>
              <small className={clsx("font-medium", error ? "text-bad" : "font-normal text-faint")}>
                {error ? t(error) : `${t("Tối thiểu")} ${formatMoneyVN(MIN)} VND, ${t("tối đa")} ${formatMoneyVN(MAX)} VND`}
              </small>
            </label>

            <div className="grid grid-cols-6 gap-1.5 max-[760px]:grid-cols-3">
              {QUICK.map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={money === v}
                  onClick={() => onAmountChange(String(v))}
                  className={clsx(
                    "rounded-lg border py-1.5 text-[13px] font-semibold tabular-nums",
                    money === v ? "border-brand bg-brand-soft text-brand" : "border-line text-muted hover:text-fg"
                  )}
                >
                  {v >= 1000000 ? `${v / 1000000} ${t("triệu")}` : `${v / 1000}k`}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-muted">{t("Ngân hàng nhận")}</span>
              {valid && banks.length ? (
                <div className="flex flex-col gap-2" role="radiogroup">
                  {banks.map((b) => {
                    const on = bankId === b._id;

                    return (
                      <button
                        key={b._id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setBankId(b._id)}
                        className={clsx(
                          "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left",
                          on ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line hover:border-brand-line"
                        )}
                      >
                        <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-surface-2 text-brand">
                          <IcBank className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <b className="block">{b.bank_name}</b>
                          <small className="block truncate text-xs tabular-nums text-muted">
                            {b.bank_number} · {b.bank_owner}
                          </small>
                        </span>
                        <span
                          className={clsx(
                            "grid h-[18px] w-[18px] flex-none place-items-center rounded-full border-2",
                            on ? "border-brand" : "border-line-2"
                          )}
                        >
                          {on && <i className="h-2 w-2 rounded-full bg-brand" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl border border-dashed border-line-2 px-3 py-3 text-[13px] text-muted">
                  <IcBank className="h-4 w-4 flex-none" />
                  {valid
                    ? banksLoading
                      ? t("Đang tải ngân hàng nhận…")
                      : t("Chưa có ngân hàng nhận cho mức nạp này")
                    : money > MAX
                    ? `${t("Mỗi lần nạp tối đa")} ${formatMoneyVN(MAX)} VND`
                    : money
                    ? `${t("Nạp tối thiểu")} ${formatMoneyVN(MIN)} VND ${t("để xem ngân hàng nhận")}`
                    : t("Nhập số tiền để xem ngân hàng nhận")}
                </div>
              )}
            </div>

            <p className="m-0 flex items-start gap-2 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[12.5px] text-muted">
              <IcInfo className="mt-px h-4 w-4 flex-none" />
              {t("Tiền đã nạp không được hoàn lại. Hãy nạp đúng số tiền bạn cần dùng để thuê máy.")}
            </p>
          </div>

          <div className="mt-auto flex flex-col gap-3 border-t border-line px-5 py-4 max-[760px]:px-4 max-[760px]:pb-4 max-[760px]:pt-3.5">
            <div className="flex flex-col gap-1 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px] tabular-nums">
              <div className="flex justify-between gap-3">
                <span className="text-muted">{t("Số dư hiện tại")}</span>
                <b>{formatMoneyVN(balance)} VND</b>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-line pt-1.5">
                <b>{t("Sau khi nạp")}</b>
                <strong className="text-lg text-brand">{formatMoneyVN(balance + (valid ? money : 0))} VND</strong>
              </div>
            </div>
            <button type="button" className={clsx(btn, btnPrimary, btnLg, "w-full")} disabled={busy} onClick={handleCreate}>
              <IcQr className="h-[18px] w-[18px]" />
              {t("Tiếp tục · Tạo mã QR")}
            </button>
          </div>
        </section>

        {/* Số dư + cách nạp */}
        <aside className="flex flex-col gap-4 max-[1100px]:grid max-[1100px]:grid-cols-2 max-[760px]:grid-cols-1">
          <div className="flex flex-col gap-3 rounded-[14px] border border-line bg-surface p-5">
            <span className="text-[12.5px] font-semibold text-muted">{t("Số dư hiện tại")}</span>
            <b className="text-[28px] font-bold leading-none tabular-nums">{formatMoneyVN(balance)} VND</b>
            <div className="grid grid-cols-3 gap-2 text-[12.5px] tabular-nums max-[400px]:grid-cols-2">
              {(
                [
                  ["Nạp trong tháng", summary ? `${formatMoneyVN(summary.month_deposit)}đ` : "—"],
                  ["Tổng đã nạp", summary ? `${formatMoneyVN(summary.total_deposit)}đ` : "—"],
                  ["Đang chờ", summary ? String(summary.pending) : "—"],
                ] as [string, string][]
              ).map(([label, value]) => (
                <div key={label} className="flex flex-col gap-0.5 rounded-[10px] bg-surface-2 px-3 py-2">
                  <span className="text-xs text-muted">{t(label)}</span>
                  <b>{value}</b>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-[14px] border border-line bg-surface p-5">
            <h3 className="text-[15px] font-bold">{t("Cách nạp")}</h3>
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {[
                ["Nhập số tiền, chọn ngân hàng", `Tối thiểu ${formatMoneyVN(MIN)} VND.`],
                ["Quét mã QR bằng app ngân hàng", "Giữ nguyên số tiền và nội dung chuyển khoản."],
                ["Bấm \"Tôi đã chuyển khoản\"", "Số dư được cộng tự động sau 1–3 phút."],
              ].map(([title, desc], i) => (
                <li key={title} className="grid grid-cols-[26px_1fr] items-start gap-2.5">
                  <span className="grid h-[26px] w-[26px] place-items-center rounded-full bg-brand-soft text-xs font-bold text-brand">
                    {i + 1}
                  </span>
                  <div>
                    <b className="block text-[13.5px] font-semibold">{t(title)}</b>
                    <span className="text-[12.5px] text-muted">{t(desc)}</span>
                  </div>
                </li>
              ))}
            </ol>
            <p className="m-0 text-xs text-faint">
              {t("Quá 15 phút chưa được cộng tiền? Bấm nút hỗ trợ ở góc phải màn hình để được trợ giúp.")}
            </p>
          </div>
        </aside>
      </div>

      <BalanceHistory reload={reload} balance={balance} summary={summary} onOpenQr={openQr} />
    </div>
  );
}

export default PaymentHistory;
